import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import PanelSkeleton from "../PanelSkeleton";
import { FaRegTrashAlt, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { FiSave } from 'react-icons/fi';
import styles from '../../styles/promopanel.module.css';

const PromoPanel = () => {
  const [code, setCode] = useState({ promocode: "", discount: "" });
  const [codes, setCodes] = useState([]);
  const [addCode, setAddCode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [codeFocus, setCodeFocus] = useState(false);
  const [discFocus, setDiscFocus] = useState(false);
  const formRef = useRef(null);

  const handleActive = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCode({ promocode: "", discount: "" });
    setAddCode(true);
  };

  const handleInactive = () => {
    if (addCode && formRef.current) {
      formRef.current.style.opacity = '0';
      formRef.current.style.transform = 'translateY(-8px)';
      setTimeout(() => setAddCode(false), 180);
    }
  };

  const fetchCodes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orders/promocode`, { withCredentials: true });
      setCodes(res.data.data);
    } catch {
      toast.error(<span className="toast">لم نتمكن من تحميل البيانات. حاول مرة اخرى</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCode(prev => ({ ...prev, [name]: value.trim().replace(/\s+/g, '') }));
  };

  const handleCodeAct = async (id, active) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/promocodeAct`, { active }, { withCredentials: true });
      await fetchCodes();
    } catch {
      toast.error(<span className="toast">لم نتمكن من تحديث الكود. حاول مرة اخرى</span>);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...code, discount: Number(code.discount) };

    try {
      confirmAlert({
        message: `إضافة كود الخصم ؟`,
        buttons: [
          {
            label: 'نعم',
            onClick: async () => {
              setSubmitting(true);
              try {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders/promocode`, payload, { withCredentials: true });
                toast.success(<span className="toast">تم إضافة كود الخصم بنجاح</span>);
                setCode({ promocode: "", discount: "" });
                setAddCode(false);
                await fetchCodes();
              } catch {
                toast.error(<span className="toast">لم نتمكن من إضافة كود الخصم. حاول مرة اخرى</span>);
              } finally {
                setSubmitting(false);
              }
            }
          },
          { label: 'لا' }
        ]
      });
    } catch {
      toast.error(<span className="toast">حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى</span>);
    }
  };

  const handleDelete = (promo) => {
    confirmAlert({
      message: `حذف كود ${promo.promocode} ؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setSubmitting(true);
            try {
              await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${promo.id}/promocode`, { withCredentials: true });
              toast.success(<span className="toast">تم حذف كود الخصم بنجاح</span>);
              await fetchCodes();
            } catch {
              toast.error(<span className="toast">لم نتمكن من حذف كود الخصم. حاول مرة اخرى</span>);
            } finally {
              setSubmitting(false);
            }
          }
        },
        { label: 'لا' }
      ]
    });
  };

  if (loading) return <PanelSkeleton count={3} />;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.tableCard}>

        {/* ── Header ── */}
        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>أكواد الخصم</span>
          {addCode ? (
            <button className={styles.cancelBtnInline} onClick={handleInactive} disabled={submitting}>
              إلغاء
            </button>
          ) : (
            <button className={styles.addBtnInline} onClick={handleActive} disabled={submitting}>
              + إضافة كود
            </button>
          )}
        </div>

        {/* ── Inline form ── */}
        {addCode && (
          <div ref={formRef} style={{ transition: 'opacity 0.18s ease, transform 0.18s ease' }}>
            <form className={styles.form} onSubmit={handleSubmit}>
              <p className={styles.formTitle}>إضافة كود خصم</p>
              <div className={styles.formRow}>
                <div className={styles.fieldWrap}>
                  <label className={`${styles.floatLabel} ${codeFocus || code.promocode ? styles.floatLabelUp : ''}`}>
                    الكود
                  </label>
                  <input
                    type="text"
                    name="promocode"
                    value={code.promocode}
                    onChange={handleChange}
                    onFocus={() => setCodeFocus(true)}
                    onBlur={() => setCodeFocus(false)}
                    maxLength={20}
                    required
                    className={styles.input}
                  />
                </div>
                <div className={styles.fieldWrap}>
                  <label className={`${styles.floatLabel} ${discFocus || code.discount ? styles.floatLabelUp : ''}`}>
                    نسبة الخصم %
                  </label>
                  <input
                    type="number"
                    step={0.01}
                    min={1}
                    max={100}
                    name="discount"
                    value={code.discount}
                    onChange={handleChange}
                    onFocus={() => setDiscFocus(true)}
                    onBlur={() => setDiscFocus(false)}
                    required
                    className={styles.input}
                  />
                </div>
                <button type="submit" disabled={submitting} className={styles.submitBtn}>
                  {submitting
                    ? <ImSpinner2 className={styles.spinner} />
                    : <FiSave className={styles.btnIcon} />
                  }
                  حفظ
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Code list ── */}
        {codes.length === 0 ? (
          <p className={styles.emptyMsg}>لا توجد أكواد خصم مضافة بعد</p>
        ) : (
          <div className={styles.codeList}>
            {codes.map((promo, i) => (
              <div
                key={promo.id}
                className={styles.codeRow}
                style={{ animationDelay: `${i * 0.04}s` }}
              >
                <span className={styles.codeText}>{promo.promocode}</span>
                <span className={styles.discountBadge}>{promo.discount}%</span>

                {promo.expires_at ? (
                  <span className={styles.expiryBadge}>ينتهي {promo.expires_at}</span>
                ) : (
                  <button
                    className={`${styles.toggleBtn} ${promo.active ? styles.toggleOn : styles.toggleOff}`}
                    onClick={() => !submitting && handleCodeAct(promo.id, promo.active)}
                    disabled={submitting}
                    title={promo.active ? "إلغاء التفعيل" : "تفعيل"}
                  >
                    {promo.active ? <FaToggleOn /> : <FaToggleOff />}
                  </button>
                )}

                <div className={styles.codeActions}>
                  {!promo.expires_at && (
                    <button
                      className={styles.deleteBtn}
                      onClick={() => !submitting && handleDelete(promo)}
                      disabled={submitting}
                    >
                      <FaRegTrashAlt />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default PromoPanel;
